'use client';

import { memo } from 'react';
import type { NodeProps } from '@xyflow/react';
import {
  useOverviewMode,
  useWorkspaceItem,
} from '@/components/workspace/store';
import { OverviewCover } from './node-frame';

/**
 * Blank screen sheet (window-management v2 Parts IV–VI, see
 * docs/internal/architecture/workspace-screens.md).
 *
 * A board is the invisible backing sheet of a BLANK SCREEN: exactly one
 * screenful, pinned to its spot on the lattice — no drag, no resize, no
 * chrome, and since Part VI NO on-canvas label at all. On the desktop
 * the sheet is completely silent; naming, numbering and renaming live
 * in the overview's label row (under the cell), and the sheet's outline
 * shows only in the overview — plus the receiving glow while a dragged
 * window would drop into it there.
 *
 * The node's wrapper is `pointer-events: none` (set in the canvas nodes
 * memo) and `draggable: false`, so panning, marquee, right-click, and
 * item interactions inside the sheet hit the pane and the contained
 * nodes exactly like open canvas. z-order: boards spawn at the back of
 * the stack, `goToItem` never raises them, so the sheet can't cover
 * the windows it hosts.
 */
function BoardNodeInner({ id }: NodeProps) {
  const item = useWorkspaceItem(id);
  const overviewMode = useOverviewMode();
  const title =
    (item?.state.cache?.title as string | undefined)?.trim() || 'Blank screen';

  return (
    <div className="relative h-full w-full">
      {/* No sheet surface here. The overview draws EVERY screen's boundary
          (app screens and blank ones) from one renderer at one cell size —
          drawing it here too meant a blank screen was a different size and
          skin from an app screen. See OverviewScreenOutlines. */}

      {/* Overview: same enter/rearrange cover as app screens (the sheet
          interior is pane-transparent, so without this a click "inside"
          the blank screen would exit the overview instead of entering
          it). */}
      {overviewMode && (
        <div className="absolute inset-0" style={{ pointerEvents: 'auto' }}>
          <OverviewCover itemId={id} title={title} hideTitle />
        </div>
      )}
    </div>
  );
}

export const BoardNode = memo(BoardNodeInner);
